import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { BillingPeriod } from '../../../clients.models';
import { env } from '../../../../../env/env';

const POLISH_DATE_FORMATS = {
  parse: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' }
  },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
    monthYearLabel: { month: 'short', year: 'numeric' },
    dateA11yLabel: { day: 'numeric', month: 'long', year: 'numeric' },
    monthYearA11yLabel: { month: 'long', year: 'numeric' }
  }
};

@Component({
  selector: 'change-billing-period-dialog',
  templateUrl: './change-billing-period-dialog.html',
  styleUrl: './change-billing-period-dialog.css',
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: POLISH_DATE_FORMATS }
  ]
})
export class ChangeBillingPeriodDialog {
  readonly data = inject<{ clientId: string, billingPeriod: BillingPeriod }>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<ChangeBillingPeriodDialog>);
  private readonly dateAdapter = inject(DateAdapter<Date>);
  private readonly httpClient = inject(HttpClient);

  public periodFrom: Date | null = this.toDate(this.data.billingPeriod?.from);
  public periodTo: Date | null = this.toDate(this.data.billingPeriod?.to);

  public ngOnInit(): void {
    this.dateAdapter.setLocale('pl-PL');
  }

  public closeDialog(): void {
    this.dialogRef.close(false);
  }

  public setQuickTo(days: number): void {
    if (!this.periodFrom) return;
    const to = new Date(this.periodFrom);
    to.setDate(to.getDate() + days);
    this.periodTo = to;
  }

  public isValid(): boolean {
    return !!this.periodFrom && !!this.periodTo;
  }

  public save(): void {
    if (!this.isValid()) return;

    this.httpClient.patch(`${env.apiUrl}/clients/${this.data.clientId}/payment/billing-period`, {
      from: this.formatDate(this.periodFrom),
      to: this.formatDate(this.periodTo)
    }).subscribe({
      next: () => this.dialogRef.close(true),
      error: (err) => {
        alert(err?.error ?? 'Błąd podczas zapisu');
        this.dialogRef.close(false);
      }
    });
  }

  private formatDate(date: Date | null): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toDate(value: Date | string | null | undefined): Date | null {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
}
